import { useMemo } from "react";
import type { EntityItemConfig, EntitySelectorConfig } from "@salestrak/ui";
import {
  FIELD_TYPE_ORDER,
  getFieldTypeMeta,
} from "#app/lib/constants/field-type-icons";
import { useFeatureFlags, FeatureFlagKey } from "#app/hooks/use-feature-flags";

/** Pool id shared by every custom-field-type EntitySelector. */
export const FIELD_TYPE_POOL_ID = "field-types";

/**
 * The custom-field types offered in create/type-picker UIs, with `photos`
 * dropped when the Photos feature flag is off for the workspace. Single source
 * for every type selector and the type-grouped fields table so the flag can't
 * be honored in one place and missed in another.
 */
export function useFieldTypeOrder(): string[] {
  const { hasFlag } = useFeatureFlags();
  const photosEnabled = hasFlag(FeatureFlagKey.PHOTOS);
  return useMemo(
    () =>
      photosEnabled
        ? FIELD_TYPE_ORDER
        : FIELD_TYPE_ORDER.filter((type) => type !== "photos"),
    [photosEnabled],
  );
}

/**
 * EntitySelector config for the "create custom field" type picker, built from
 * {@link useFieldTypeOrder} so `photos` disappears with the feature flag. Used
 * by every create flow (settings page, manage dialog, form dialog).
 */
export function useFieldTypeSelectorConfig(): EntitySelectorConfig {
  const fieldTypeOrder = useFieldTypeOrder();
  return useMemo(() => {
    const items: EntityItemConfig[] = fieldTypeOrder.map((type) => {
      const meta = getFieldTypeMeta(type);
      return {
        id: type,
        title: meta.label,
        avatar: { icon: meta.icon, color: meta.color, padded: true },
      };
    });
    return {
      pools: [{ id: FIELD_TYPE_POOL_ID, label: "Field Types", items }],
      maxSelections: 1,
      searchPlaceholder: "Search field types...",
      emptyMessage: "No matching field types",
    };
  }, [fieldTypeOrder]);
}
